import React, { useEffect, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import {
  Card, Button, Spin, message, Row, Col, Tag, Descriptions, Space, Alert,
  Checkbox, Statistic, Upload,
} from 'antd';
import { CameraOutlined, PauseCircleOutlined } from '@ant-design/icons';
import LayoutWrapper from '@iso/components/utility/layoutWrapper.js';
import Box from '@iso/components/utility/box';
import { sprayService, weatherDataService } from '@iso/services';
import { spraySessionService } from '@iso/services/spraySession.service';
import { apiEndpoint } from '@iso/lib/helpers/endpoint';
import { authHeader } from '@iso/lib/helpers/authHeader';
import { getSprayJobStatusLabel, getSprayJobTagColor } from '@iso/constants/sprayJobStates';
import PPEChecklist from './PPEChecklist';
import PauseZoneModal from './PauseZoneModal';
import useSprayRequiredSections from '../../../hooks/useSprayRequiredSections';

/**
 * Full-page Spray step (replaces PrepareSprayModal / TaskZoneExecutionModal).
 *
 * Handles two transitions:
 *   - PREPARED   → SPRAYING       via sprayService.startSpraying
 *   - SPRAYING   → SPRAY_CLEANUP  via sprayService.markAsSprayCleanup
 *
 * Zone progress / refills are still recorded server-side via the existing
 * stop-spraying / refill endpoints; this page surfaces them when present.
 */
const SprayJobSpray = () => {
  const { id } = useParams();
  const history = useHistory();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  // Pre-start gates restored from the old PrepareSprayModal: operator must
  // confirm each piece of equipment meets requirements + tick every PPE item.
  const [equipmentChecks, setEquipmentChecks] = useState({
    nozzle: false,
    tractor: false,
    sprayer: false,
  });
  const [ppeChecked, setPpeChecked] = useState(false);
  // In-flight spraying-phase tasks the operator ticks off while spraying.
  // These are client-side only for now — they're not persisted on the job
  // record because the existing task storage doesn't track per-task
  // completion state. Useful as a checklist nonetheless.
  const [sprayingTasksChecked, setSprayingTasksChecked] = useState([]);
  // Live current weather readings (visible safety check before Start).
  const [currentWeather, setCurrentWeather] = useState(null);
  // Pause flow — when the operator hits Pause we open PauseZoneModal so
  // they capture where they stopped before flipping the job back to
  // PREPARED. Mirrors SprayJobDetail's pause wiring; the page lives on
  // the operator's path so they no longer have to detour to the detail
  // page for the feature (PR #417 follow-up: "certain people do not see
  // this pause feature, investigate" — root cause was the detail page's
  // isPrivileged gate; cure is exposing it on the Spray page too).
  const [pauseModalOpen, setPauseModalOpen] = useState(false);
  // Equipment photo uploads (nozzle/tractor/sprayer) — agronomist admin
  // controls whether these are required via the "Equipment photos" toggle.
  const [equipmentPhotos, setEquipmentPhotos] = useState({
    nozzle: null,
    tractor: null,
    sprayer: null,
  });
  const { sections: req } = useSprayRequiredSections(job?.property_id);

  const reload = () => {
    setLoading(true);
    return sprayService
      .getDetail(id)
      .then((res) => setJob(res?.spray_job || null))
      .catch(() => message.error('Failed to load spray job'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    let cancelled = false;
    sprayService
      .getDetail(id)
      .then((res) => !cancelled && setJob(res?.spray_job || null))
      .catch(() => !cancelled && message.error('Failed to load spray job'))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [id]);

  // Pull live weather as soon as we know the property. Same shape as the old
  // PrepareSprayModal so existing operators see familiar fields.
  useEffect(() => {
    const propertyId = job?.property_id;
    if (!propertyId) return;
    let cancelled = false;
    weatherDataService
      .getRealtimeWeatherData(propertyId)
      .then((res) => {
        if (cancelled) return;
        const values = res?.data?.values;
        if (!values) {
          setCurrentWeather({ wind: null, deltaT: null, rain: null, temperature: null, humidity: null });
          return;
        }
        // Delta T uses the standard agronomic humidity-only approximation
        // ΔT ≈ (100 − humidity) / 5. Previously we did
        // `temperatureApparent − temperature` which is the feels-like delta,
        // not wet-bulb depression — produced near-zero values that meant
        // nothing to operators.
        const humidity = values.humidity != null
          ? Number(values.humidity.toFixed(2))
          : null;
        const deltaT = humidity != null
          ? Number(((100 - humidity) / 5).toFixed(2))
          : null;
        setCurrentWeather({
          wind: values.windSpeed != null ? Number((values.windSpeed * 3.6).toFixed(2)) : null,
          deltaT,
          rain: values.rainIntensity != null ? Number(values.rainIntensity.toFixed(2)) : null,
          temperature: values.temperature != null ? Number(values.temperature.toFixed(2)) : null,
          humidity,
        });
      })
      .catch((err) => {
        if (!cancelled) {
          console.error('Failed to fetch realtime weather:', err);
          setCurrentWeather({ wind: null, deltaT: null, rain: null, temperature: null, humidity: null });
        }
      });
    return () => { cancelled = true; };
  }, [job?.property_id]);

  // Equipment image upload helper — wraps antd's Upload onChange to extract
  // the image id returned by the existing /images endpoint and stash it under
  // the appropriate equipment key.
  const handleEquipmentUpload = (key) => (info) => {
    const file = info?.file;
    if (!file) return;
    if (file.status === 'uploading') return;
    if (file.status === 'done') {
      const imageData = file.response?.image;
      if (file.response?.code === '0000' && imageData) {
        setEquipmentPhotos((p) => ({
          ...p,
          [key]: {
            uid: file.uid,
            name: file.name,
            imageId: imageData.id,
            url: imageData.full_path,
          },
        }));
        message.success(`${file.name} uploaded`);
      } else {
        message.error(`Upload failed: ${file.response?.message || 'Unknown error'}`);
      }
    } else if (file.status === 'error') {
      message.error(`${file.name} upload failed.`);
    } else if (file.status === 'removed') {
      setEquipmentPhotos((p) => ({ ...p, [key]: null }));
    }
  };

  // Antd Upload props — restricts to JPG/PNG under 2MB, mirrors the old
  // PrepareSprayModal validation.
  const uploadProps = {
    name: 'Image[file]',
    action: apiEndpoint.images,
    headers: authHeader(true),
    listType: 'picture',
    multiple: false,
    beforeUpload: (file) => {
      const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png';
      if (!isJpgOrPng) {
        message.error('You can only upload JPG/PNG file!');
        return false;
      }
      if (file.size / 1024 / 1024 >= 2) {
        message.error('Image must smaller than 2MB!');
        return false;
      }
      return true;
    },
  };

  const handleStart = async () => {
    setBusy(true);
    try {
      // Persist any uploaded equipment photos to spray_sessions before
      // flipping status. Behaviour matches the old PrepareSprayModal —
      // we only POST when at least one image was uploaded.
      const photoPayload = {};
      if (equipmentPhotos.nozzle?.imageId) photoPayload.nozzle_image_id = equipmentPhotos.nozzle.imageId;
      if (equipmentPhotos.tractor?.imageId) photoPayload.tractor_image_id = equipmentPhotos.tractor.imageId;
      if (equipmentPhotos.sprayer?.imageId) photoPayload.sprayer_image_id = equipmentPhotos.sprayer.imageId;
      if (Object.keys(photoPayload).length > 0) {
        try {
          await spraySessionService.createOrUpdate({ spray_job_id: Number(id), ...photoPayload });
        } catch (err) {
          // Don't block start on photo-persistence failures — surface and continue.
          console.error('Failed to save equipment photos:', err);
          message.warning('Equipment photos were uploaded but could not be linked to the session.');
        }
      }

      const res = await sprayService.startSpraying(id);
      if (res?.code === '0000') {
        message.success('Spraying started');
        await reload();
      } else {
        message.error(res?.message || 'Failed to start spraying');
      }
    } catch (err) {
      message.error(err?.response?.data?.message || 'Failed to start spraying');
    } finally {
      setBusy(false);
    }
  };

  const handlePauseSubmit = async (pauseZone) => {
    setBusy(true);
    try {
      const res = await sprayService.stopSpraying(id, pauseZone);
      if (res?.code === '0000') {
        message.success('Spray paused — pause point logged');
        setPauseModalOpen(false);
        // Reload so the status tag flips back to Prepared and the
        // Start Spraying button reappears when the operator resumes.
        reload();
      } else {
        message.error(res?.message || 'Failed to pause spray job');
      }
    } catch (err) {
      message.error(err?.response?.data?.message || 'Failed to pause spray job');
    } finally {
      setBusy(false);
    }
  };

  const handleMoveToCleanup = async () => {
    setBusy(true);
    try {
      const res = await sprayService.markAsSprayCleanup(id);
      if (res?.code === '0000') {
        message.success('Moved to cleanup');
        history.push(`/spray-job/${id}/cleanup`);
      } else {
        message.error(res?.message || 'Failed to move job to cleanup');
      }
    } catch (err) {
      message.error(err?.response?.data?.message || 'Failed to move job to cleanup');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <LayoutWrapper>
        <div style={{ textAlign: 'center', margin: '60px 0' }}>
          <Spin size="large" />
        </div>
      </LayoutWrapper>
    );
  }
  if (!job) {
    return (
      <LayoutWrapper>
        <Box>Spray job not found.</Box>
      </LayoutWrapper>
    );
  }

  const status = job.status;
  const canStart = status === 'prepared';
  const canMoveToCleanup = status === 'spraying';
  // Replicates the old PrepareSprayModal gate, but every leg is bypassable
  // via the agronomist-admin "Required Confirmations" toggles.
  const allEquipmentChecked = Object.values(equipmentChecks).every(Boolean);
  const allPhotosUploaded = Object.values(equipmentPhotos).every((p) => p && p.imageId);
  const equipmentGate = !req.equipment_checks || allEquipmentChecked;
  const ppeGate = !req.ppe_spraying || ppeChecked;
  const photosGate = !req.equipment_photos || allPhotosUploaded;
  const startGateOpen = canStart && equipmentGate && ppeGate && photosGate;

  // Decide whether a current-weather reading is within the job's configured
  // limits. Returns 'in' / 'out' / 'unknown'. Used to colour the Current
  // Weather card (green when in scope, red when out of scope) so the
  // operator gets an at-a-glance go/no-go before pressing Start Spraying.
  const scope = (value, min, max) => {
    if (value == null) return 'unknown';
    if (min != null && value < min) return 'out';
    if (max != null && value > max) return 'out';
    if (min == null && max == null) return 'unknown';
    return 'in';
  };
  const colourForScope = (s) => s === 'in' ? '#3f8600' : s === 'out' ? '#cf1322' : undefined;
  const windScope = scope(currentWeather?.wind, job.wind_limit_min, job.wind_limit_max);
  const deltaTScope = scope(currentWeather?.deltaT, job.delta_t_limit_min, job.delta_t_limit_max);
  // Rain is a max-only limit — anything ≤ the configured rain_limit is in scope.
  const rainScope = scope(currentWeather?.rain, null, job.rain_limit);
  const tempScope = scope(currentWeather?.temperature, job.temperature_min, job.temperature_max);

  return (
    <LayoutWrapper>
      <Box style={{ marginBottom: 16 }}>
        <Row justify="space-between" align="middle">
          <Col>
            <h2 style={{ margin: 0 }}>Spray: {job.job_name}</h2>
            <Tag color={getSprayJobTagColor(status)} style={{ marginTop: 8 }}>
              {getSprayJobStatusLabel(status)}
            </Tag>
          </Col>
          <Col>
            <Space>
              <Button onClick={() => history.push('/spray')}>Back</Button>
              {canStart && (
                <Button
                  type="primary"
                  loading={busy}
                  disabled={!startGateOpen}
                  onClick={handleStart}
                >
                  Start Spraying
                </Button>
              )}
              {canMoveToCleanup && (
                <>
                  <Button
                    icon={<PauseCircleOutlined />}
                    loading={busy}
                    onClick={() => setPauseModalOpen(true)}
                  >
                    Pause
                  </Button>
                  <Button type="primary" loading={busy} onClick={handleMoveToCleanup}>
                    Move to Cleanup
                  </Button>
                </>
              )}
            </Space>
          </Col>
        </Row>
      </Box>

      {/* Pause modal — capture where in the field the operator stopped
          so a future resume knows the start coordinates. Available to
          any operator on the job, not gated on isPrivileged. */}
      <PauseZoneModal
        visible={pauseModalOpen}
        mode="pause"
        job={job}
        loading={busy}
        onCancel={() => setPauseModalOpen(false)}
        onSubmit={handlePauseSubmit}
      />

      <Card title="Job Details" style={{ marginBottom: 16 }}>
        <Descriptions column={{ xs: 1, sm: 2, md: 3 }} size="small">
          <Descriptions.Item label="Type">{job.job_type}</Descriptions.Item>
          <Descriptions.Item label="Water Quantity">
            {job.water_quantity ? `${job.water_quantity} L` : '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Started">{job.start_time || '—'}</Descriptions.Item>
        </Descriptions>
      </Card>

      {/* Zones — visible during both Prepared and Spraying so the operator
          can reference the field locations from this page (stakeholder
          feedback: zones were missing on the Spray page entirely). */}
      <Card title="Zones" style={{ marginBottom: 16 }}>
        {(() => {
          const zones = Array.isArray(job.zones)
            ? job.zones
            : (() => {
                try {
                  return job.zones ? JSON.parse(job.zones) : [];
                } catch (e) {
                  // Log so a malformed zones field surfaces in error
                  // reporting instead of just showing "No zones recorded"
                  // — that message would otherwise mislead operators.
                  console.error('SprayJobSpray: failed to parse zones JSON', e);
                  return [];
                }
              })();
          if (!zones.length) {
            return <span style={{ color: '#666' }}>No zones recorded for this job.</span>;
          }
          const formatPoint = (letter, n1, n2) =>
            [letter, n1 ? `B${n1}` : null, n2 ? `R${n2}` : null]
              .filter(Boolean)
              .join(' · ') || '—';
          // Heterogeneous items list: zones + refill markers share order.
          // We number each kind separately so operators see "Zone 3" /
          // "Refill 1" rather than a single mixed counter.
          let zoneCount = 0;
          let refillCount = 0;
          return zones.map((z, i) => {
            if (z && z.type === 'refill') {
              refillCount += 1;
              return (
                <div
                  key={`refill-${i}`}
                  style={{
                    padding: '8px 12px',
                    marginBottom: 8,
                    borderRadius: 6,
                    background: '#f6ffed',
                    border: '1px solid #b7eb8f',
                  }}
                >
                  <strong>Refill {refillCount}</strong>{' '}
                  <span style={{ color: '#666' }}>
                    {z.liters != null ? `${z.liters} L` : '—'}
                  </span>
                </div>
              );
            }
            zoneCount += 1;
            return (
              <div
                // Read-only list, never reordered — array index is a safe
                // key here but we tack the zone's start coords on too so a
                // future zone-id migration drops in without churning keys.
                key={`${z.startLetter || ''}-${z.startNumber1 || ''}-${z.startNumber2 || ''}-${i}`}
                style={{
                  padding: '8px 12px',
                  marginBottom: 8,
                  borderRadius: 6,
                  background: '#fafafa',
                  border: '1px solid #eee',
                }}
              >
                <strong>Zone {zoneCount}</strong>{' '}
                <span style={{ color: '#666' }}>
                  {formatPoint(z.startLetter, z.startNumber1, z.startNumber2)}
                  {' → '}
                  {formatPoint(z.finishLetter, z.finishNumber1, z.finishNumber2)}
                </span>
                {z.comment && (
                  <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
                    {z.comment}
                  </div>
                )}
              </div>
            );
          });
        })()}
      </Card>

      {/* Pre-start gates — only shown while the job is still PREPARED. */}
      {canStart && (
        <>
          <Card title="Current Weather" style={{ marginBottom: 16 }}>
            <Row gutter={[16, 16]}>
              <Col xs={12} sm={8} md={{ flex: '20%' }}>
                <Statistic
                  title="Wind"
                  value={currentWeather?.wind ?? '—'}
                  suffix={currentWeather?.wind != null ? 'km/h' : ''}
                  valueStyle={{ color: colourForScope(windScope) }}
                />
              </Col>
              <Col xs={12} sm={8} md={{ flex: '20%' }}>
                <Statistic
                  title="Delta T"
                  value={currentWeather?.deltaT ?? '—'}
                  /* Stakeholder direction (PR #417 follow-up): Delta T
                     is shown as a flat number; the °C suffix added more
                     noise than meaning when the operator just needs to
                     compare against the job's Delta T limits. */
                  valueStyle={{ color: colourForScope(deltaTScope) }}
                />
              </Col>
              <Col xs={12} sm={8} md={{ flex: '20%' }}>
                <Statistic
                  title="Temperature"
                  value={currentWeather?.temperature ?? '—'}
                  suffix={currentWeather?.temperature != null ? '°C' : ''}
                  valueStyle={{ color: colourForScope(tempScope) }}
                />
              </Col>
              <Col xs={12} sm={8} md={{ flex: '20%' }}>
                <Statistic
                  title="Humidity"
                  value={currentWeather?.humidity ?? '—'}
                  suffix={currentWeather?.humidity != null ? '%' : ''}
                />
              </Col>
              <Col xs={12} sm={8} md={{ flex: '20%' }}>
                <Statistic
                  title="Rain"
                  value={currentWeather?.rain ?? '—'}
                  suffix={currentWeather?.rain != null ? 'mm/hr' : ''}
                  valueStyle={{ color: colourForScope(rainScope) }}
                />
              </Col>
            </Row>
            <div style={{ marginTop: 8, fontSize: 12, color: '#888' }}>
              <span style={{ color: '#3f8600' }}>Green</span> = in scope ·
              {' '}
              <span style={{ color: '#cf1322' }}>Red</span> = outside the job's
              configured limits.
            </div>
            {currentWeather?.deltaT == null && (
              <Alert
                type="warning"
                showIcon
                style={{ marginTop: 12 }}
                message="Delta T data is unavailable — verify weather conditions before starting."
              />
            )}
          </Card>

          {req.equipment_checks && (
          <Card title="Equipment Checks" style={{ marginBottom: 16 }}>
            <Row gutter={[16, 12]}>
              <Col xs={24} sm={8}>
                <strong style={{ display: 'block', marginBottom: 6 }}>Nozzle</strong>
                <div style={{ color: '#666', marginBottom: 8 }}>
                  {job.nozzle?.title || '—'}
                </div>
                <Checkbox
                  checked={equipmentChecks.nozzle}
                  onChange={(e) =>
                    setEquipmentChecks((p) => ({ ...p, nozzle: e.target.checked }))
                  }
                >
                  Equipment meets requirements
                </Checkbox>
              </Col>
              <Col xs={24} sm={8}>
                <strong style={{ display: 'block', marginBottom: 6 }}>Tractor</strong>
                <div style={{ color: '#666', marginBottom: 8 }}>
                  {job.tractor?.title || '—'}
                </div>
                <Checkbox
                  checked={equipmentChecks.tractor}
                  onChange={(e) =>
                    setEquipmentChecks((p) => ({ ...p, tractor: e.target.checked }))
                  }
                >
                  Equipment meets requirements
                </Checkbox>
              </Col>
              <Col xs={24} sm={8}>
                <strong style={{ display: 'block', marginBottom: 6 }}>Sprayer</strong>
                <div style={{ color: '#666', marginBottom: 8 }}>
                  {job.sprayer?.title || '—'}
                </div>
                <Checkbox
                  checked={equipmentChecks.sprayer}
                  onChange={(e) =>
                    setEquipmentChecks((p) => ({ ...p, sprayer: e.target.checked }))
                  }
                >
                  Equipment meets requirements
                </Checkbox>
              </Col>
            </Row>
          </Card>
          )}

          {/* Equipment Photos — render whenever uploads are allowed; only
              gating happens when the toggle is set to required. */}
          <Card title="Equipment Photos" style={{ marginBottom: 16 }}>
            <Row gutter={[16, 12]}>
              {['nozzle', 'tractor', 'sprayer'].map((key) => (
                <Col key={key} xs={24} sm={8}>
                  <strong style={{ display: 'block', marginBottom: 6, textTransform: 'capitalize' }}>
                    {key}
                  </strong>
                  <Upload
                    {...uploadProps}
                    fileList={equipmentPhotos[key] ? [equipmentPhotos[key]] : []}
                    onChange={handleEquipmentUpload(key)}
                    onRemove={() => {
                      setEquipmentPhotos((p) => ({ ...p, [key]: null }));
                      return true;
                    }}
                  >
                    <Button icon={<CameraOutlined />}>
                      {equipmentPhotos[key] ? 'Replace photo' : 'Take photo'}
                    </Button>
                  </Upload>
                </Col>
              ))}
            </Row>
            {!req.equipment_photos && (
              <div style={{ marginTop: 8, color: '#888', fontSize: 12 }}>
                Optional — your agronomist admin hasn't made photo uploads required.
              </div>
            )}
          </Card>

          {req.ppe_spraying && (
            <PPEChecklist job={job} phase="spraying" onChange={setPpeChecked} />
          )}
        </>
      )}

      {/* In-flight spray tasks — visible while the job is currently being
          sprayed so the operator can tick them off as they go. Not a gate
          for Move to Cleanup (intentional — tasks may legitimately roll
          over to cleanup), purely an operator checklist. */}
      {status === 'spraying' && (
        <Card title="Spraying Tasks" style={{ marginBottom: 16 }}>
          {(() => {
            // Accept both `prepared` (the PermissionTaskModal dropdown's
            // "Spray" option backing value, since the dropdown labels the
            // status the job leaves from rather than the page the task
            // shows on) and `spraying` (legacy data + the old "Cleanup"
            // option, kept here so existing jobs still surface their
            // tasks while operators migrate to the renamed options).
            const sprayingTasks = (job.tasks || []).filter(
              (t) => t.mapping_status === 'prepared' || t.mapping_status === 'spraying'
            );
            if (sprayingTasks.length === 0) {
              return (
                <span style={{ color: '#666' }}>
                  No tasks assigned to the spraying phase.
                </span>
              );
            }
            return sprayingTasks.map((task) => (
              <div key={task.task_name} style={{ marginBottom: 8 }}>
                <Checkbox
                  checked={sprayingTasksChecked.includes(task.task_name)}
                  onChange={(e) =>
                    setSprayingTasksChecked((prev) =>
                      e.target.checked
                        ? [...prev, task.task_name]
                        : prev.filter((t) => t !== task.task_name)
                    )
                  }
                >
                  {task.task_name}
                </Checkbox>
              </div>
            ));
          })()}
        </Card>
      )}

      {!canStart && !canMoveToCleanup && (
        <Alert
          type="info"
          showIcon
          message="This job is not in an actionable spray state."
          description="Return to the dashboard to pick the right step for the job's current status."
        />
      )}
    </LayoutWrapper>
  );
};

export default SprayJobSpray;
